/**
 * QuickSearch — Cmd/Ctrl+K global modal for fast director or company lookup
 * ==========================================================================
 * Mount once at app root. Listens for Cmd+K / Ctrl+K. Two tabs:
 *   • Director: takes a name → routes to /mca21/directors with prefill
 *   • Company:  takes name or CIN → routes to /mca21 with prefill
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Users, Briefcase, X, ArrowRight, Command } from "lucide-react";

export default function QuickSearch() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("director");
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  // Cmd+K / Ctrl+K global shortcut
  useEffect(() => {
    const onKey = (e) => {
      const k = e.key?.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && k === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("rudrik:open-quicksearch", () => setOpen(true));
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const submit = useCallback(() => {
    const q = query.trim();
    if (!q) return;
    if (tab === "director") {
      navigate(`/mca21/directors?prefill=${encodeURIComponent(q)}`);
    } else {
      navigate(`/mca21?prefill=${encodeURIComponent(q)}`);
    }
    setOpen(false);
    setQuery("");
  }, [query, tab, navigate]);

  if (!open) return null;

  return (
    <div
      data-testid="quicksearch-modal"
      onClick={() => setOpen(false)}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        zIndex: 200, padding: "10vh 20px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 560, background: "#0d1117", border: "1px solid #1e2530",
          borderRadius: 14, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
        }}
      >
        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #1e2530" }}>
          {[
            { id: "director", icon: Users, label: "Director" },
            { id: "company", icon: Briefcase, label: "Company / CIN" },
          ].map((t) => {
            const T = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`quicksearch-tab-${t.id}`}
                style={{
                  flex: 1, background: "transparent", border: "none", borderBottom: active ? "2px solid #a855f7" : "2px solid transparent",
                  color: active ? "#f1f5f9" : "#64748b", padding: "13px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
                }}
              >
                <T size={13} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Input */}
        <div style={{ padding: 18, display: "flex", alignItems: "center", gap: 10 }}>
          <Search size={18} style={{ color: "#94a3b8" }} />
          <input
            data-testid="quicksearch-input"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder={tab === "director" ? "e.g. Rajesh Sharma, Mehul Choksi, Ratan Tata" : "e.g. Infosys, U72200KA2019PTC123456"}
            style={{
              flex: 1, background: "transparent", border: "none", outline: "none",
              color: "#f1f5f9", fontSize: 16,
            }}
          />
          <button
            onClick={() => setOpen(false)}
            data-testid="quicksearch-close"
            style={{ background: "transparent", border: "1px solid #1e2530", borderRadius: 5, color: "#94a3b8", padding: "5px 8px", fontSize: 10, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <X size={11} /> Esc
          </button>
        </div>

        {/* Footer */}
        <div style={{ padding: "10px 18px", background: "#080c12", borderTop: "1px solid #1e2530", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, color: "#64748b" }}>
          <span><Command size={11} style={{ display: "inline", marginRight: 4 }} />K to toggle · Esc to close</span>
          <button
            data-testid="quicksearch-submit"
            onClick={submit}
            disabled={!query.trim()}
            style={{
              background: query.trim() ? "linear-gradient(135deg, #a855f7, #7c3aed)" : "#1e2530",
              color: "#fff", border: "none", padding: "7px 14px", borderRadius: 6,
              fontSize: 12, fontWeight: 700, cursor: query.trim() ? "pointer" : "not-allowed",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            Search <ArrowRight size={11} />
          </button>
        </div>
      </div>
    </div>
  );
}
